import { useState, useEffect } from 'react';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Button } from 'src/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  DotsSixVerticalIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { T, useGT } from 'gt-react';

import {
  formatConditionValue,
  isListOperator,
  parseConditionValue,
} from '@/utils/conditionValues';

const OPERATORS = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'is_null',
  'is_not_null',
  'in',
  'not_in',
  'regex_match',
] as const;

const OPERATOR_LABELS: Record<string, string> = {
  eq: '= (equals)',
  neq: '!= (not equals)',
  gt: '> (greater than)',
  gte: '>= (greater or equal)',
  lt: '< (less than)',
  lte: '<= (less or equal)',
  contains: 'contains',
  not_contains: 'not contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_null: 'is null',
  is_not_null: 'is not null',
  in: 'in (list)',
  not_in: 'not in (list)',
  regex_match: 'regex match',
};

export interface FilterCondition {
  field: string;
  operator: string;
  value?: unknown;
}

export interface FilterRule {
  conditions: FilterCondition[];
}

interface FilterRulesEditorProps {
  filterRules: FilterRule[];
  filterMode: string;
  onFilterRulesChange: (rules: FilterRule[]) => void;
  onFilterModeChange: (mode: string) => void;
  description?: string;
}

/**
 * Input that keeps raw text in local state while typing and only
 * parses + persists the typed value on blur. This prevents snap-back
 * issues (e.g., trailing commas or decimal points disappearing).
 */
function ConditionValueInput({
  value,
  operator,
  onCommit,
}: {
  value: unknown;
  operator: string;
  onCommit: (parsed: unknown) => void;
}) {
  const gt = useGT();
  const [raw, setRaw] = useState(() => formatConditionValue(value));

  // Sync from parent when the value changes externally (e.g., operator switch)
  useEffect(() => {
    setRaw(formatConditionValue(value));
  }, [value]);

  return (
    <Input
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => onCommit(parseConditionValue(raw, operator))}
      placeholder={
        isListOperator(operator)
          ? gt('Comma-separated values', {
              context:
                'filter rules condition value input placeholder for list operators',
            })
          : gt('Value', {
              context: 'filter rules condition value input placeholder',
            })
      }
      className="text-xs h-8"
    />
  );
}

/**
 * A single, drag-sortable filter rule (group of AND conditions). The grip handle
 * carries the drag listeners; the rest of the card stays interactive.
 */
function SortableFilterRule({
  id,
  ruleIndex,
  rule,
  onRemoveRule,
  onUpdateCondition,
  onAddCondition,
  onRemoveCondition,
}: {
  id: string;
  ruleIndex: number;
  rule: FilterRule;
  onRemoveRule: (ruleIndex: number) => void;
  onUpdateCondition: (
    ruleIndex: number,
    condIndex: number,
    updates: Partial<FilterCondition>,
  ) => void;
  onAddCondition: (ruleIndex: number) => void;
  onRemoveCondition: (ruleIndex: number, condIndex: number) => void;
}) {
  const gt = useGT();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="space-y-2 rounded border bg-muted/30 p-2"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="cursor-grab text-muted-foreground active:cursor-grabbing"
            aria-label={gt('Drag to reorder rule', {
              context: 'filter rules reorder handle',
            })}
            {...attributes}
            {...listeners}
          >
            <DotsSixVerticalIcon className="w-3.5 h-3.5" />
          </button>
          <p className="text-xs font-medium text-muted-foreground">
            {gt('Rule {n}', {
              context: 'filter rules editor rule number label',
              n: String(ruleIndex + 1),
            })}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={() => onRemoveRule(ruleIndex)}
        >
          <TrashIcon className="w-3 h-3" />
        </Button>
      </div>
      {rule.conditions.map((cond, condIndex) => (
        <div key={condIndex} className="flex items-center gap-2">
          <Input
            value={cond.field}
            onChange={(e) =>
              onUpdateCondition(ruleIndex, condIndex, {
                field: e.target.value,
              })
            }
            placeholder={gt('Field', {
              context: 'filter rules condition field input placeholder',
            })}
            className="text-xs h-8"
          />
          <Select
            value={cond.operator}
            onValueChange={(v) =>
              onUpdateCondition(ruleIndex, condIndex, { operator: v })
            }
          >
            <SelectTrigger className="w-[160px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPERATORS.map((op) => (
                <SelectItem key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {cond.operator !== 'is_null' && cond.operator !== 'is_not_null' && (
            <ConditionValueInput
              value={cond.value}
              operator={cond.operator}
              onCommit={(parsed) =>
                onUpdateCondition(ruleIndex, condIndex, {
                  value: parsed,
                })
              }
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 shrink-0"
            onClick={() => onRemoveCondition(ruleIndex, condIndex)}
          >
            <TrashIcon className="w-3 h-3" />
          </Button>
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs"
        onClick={() => onAddCondition(ruleIndex)}
      >
        <PlusIcon className="w-3 h-3 mr-1" />
        <T context="filter rules add condition button">Add Condition</T>
      </Button>
    </div>
  );
}

export function FilterRulesEditor({
  filterRules,
  filterMode,
  onFilterRulesChange,
  onFilterModeChange,
  description = 'Filter rows. OR between rules, AND within conditions.',
}: FilterRulesEditorProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const addFilterRule = () => {
    onFilterRulesChange([
      ...filterRules,
      { conditions: [{ field: '', operator: 'eq', value: '' }] },
    ]);
  };

  const removeFilterRule = (ruleIndex: number) => {
    onFilterRulesChange(filterRules.filter((_, i) => i !== ruleIndex));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(active.id);
    const newIndex = Number(over.id);
    if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;
    onFilterRulesChange(arrayMove(filterRules, oldIndex, newIndex));
  };

  const updateFilterCondition = (
    ruleIndex: number,
    condIndex: number,
    updates: Partial<FilterCondition>,
  ) => {
    const updated = filterRules.map((rule, ri) =>
      ri === ruleIndex
        ? {
            ...rule,
            conditions: rule.conditions.map((c, ci) => {
              if (ci !== condIndex) return c;
              const merged = { ...c, ...updates };

              // When switching operator TO in/not_in, convert string value to array
              if (
                updates.operator &&
                isListOperator(updates.operator) &&
                !isListOperator(c.operator)
              ) {
                const str = String(merged.value ?? '');
                merged.value = str
                  ? str
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : [];
              }

              // When switching operator FROM in/not_in, convert array value to string
              if (
                updates.operator &&
                !isListOperator(updates.operator) &&
                isListOperator(c.operator)
              ) {
                merged.value = Array.isArray(merged.value)
                  ? merged.value.join(', ')
                  : String(merged.value ?? '');
              }

              return merged;
            }),
          }
        : rule,
    );
    onFilterRulesChange(updated);
  };

  const addFilterCondition = (ruleIndex: number) => {
    const updated = filterRules.map((rule, ri) =>
      ri === ruleIndex
        ? {
            ...rule,
            conditions: [
              ...rule.conditions,
              { field: '', operator: 'eq' as const, value: '' },
            ],
          }
        : rule,
    );
    onFilterRulesChange(updated);
  };

  const removeFilterCondition = (ruleIndex: number, condIndex: number) => {
    const updated = filterRules.map((rule, ri) =>
      ri === ruleIndex
        ? {
            ...rule,
            conditions: rule.conditions.filter((_, ci) => ci !== condIndex),
          }
        : rule,
    );
    onFilterRulesChange(updated);
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">
            <T context="workflows">Filter Rules</T>
          </Label>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>
        <Select value={filterMode} onValueChange={onFilterModeChange}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="include">
              <T context="workflows">Include</T>
            </SelectItem>
            <SelectItem value="exclude">
              <T context="workflows">Exclude</T>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={filterRules.map((_, i) => String(i))}
          strategy={verticalListSortingStrategy}
        >
          {filterRules.map((rule, ruleIndex) => (
            <SortableFilterRule
              key={ruleIndex}
              id={String(ruleIndex)}
              ruleIndex={ruleIndex}
              rule={rule}
              onRemoveRule={removeFilterRule}
              onUpdateCondition={updateFilterCondition}
              onAddCondition={addFilterCondition}
              onRemoveCondition={removeFilterCondition}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={addFilterRule}
      >
        <PlusIcon className="w-3 h-3 mr-1" />
        <T context="filter rules add filter rule button">Add Filter Rule</T>
      </Button>
    </div>
  );
}
